import { useFormContext } from 'react-hook-form';
import { SVG } from '../../../../../constants/assetLists';
import { useOnBoardingContext } from '../../../../../context/onBoarding/OnBoardingContextProvider';
import { NetworkDetailsFormValues, NetworkNodeFieldPath } from '../types';
import AppButton from '../../../../../components/common/AppButton';
import { useState } from 'react';
import { useSnackbar } from '../../../../../context/SnackbarContextProvider';
import ScreensSidebar, { SIDEBAR_TABS } from './ScreensSidebar';
import NetworkSetupNavBar from './NetworkSetupNavBar';
import Departments from './tab-screens/department/Departments';
import Roles from './tab-screens/roles/Roles';
import DepartmentHeader from './tab-screens/department/DepartmentHeader';
import RolesHeader from './tab-screens/roles/RolesHeader';
import ShiftsHeader from './tab-screens/shifts/ShiftsHeader';
import Shifts from './tab-screens/shifts/Shifts';
import ZonesHeader from './tab-screens/zones/ZonesHeader';
import Zones from './tab-screens/zones/Zones';
import ChecklistsHeader from './tab-screens/checklists/ChecklistsHeader';
import Checklists from './tab-screens/checklists/Checklists';
import UsersHeader from './tab-screens/users/usersHeader';
import Users from './tab-screens/users/Users';
import RegulationsHeader from './tab-screens/regulations/RegulationsHeader';
import Regulations from './tab-screens/regulations/Regulations';
import {
  handleActivateOnBoardingDraftService,
  handleUpsertOnBoardingDraftService,
} from '../../../../../services/on-boarding';
import { buildOnBoardingDraftPayload } from '../../../buildOnBoardingDraftPayload';
import {
  departmentSchema,
  roleSchema,
  shiftSchema,
  zoneSchema,
  checklistSchema,
  regulationSchema,
  filterInvalidNetworkTree,
} from '../networkDetailsSchema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import { useNavigate } from 'react-router';
import FullScreenLoader from '../../../../../components/common/FullScreenLoader';
import { useNetworkDetailsDraft } from '../NetworkDetailsDraftContext';
import { isSuperAdminOnly } from '../../../../../utils';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

export type TabScreens =
  | 'departments'
  | 'roles'
  | 'shifts'
  | 'zones'
  | 'users'
  | 'checklists'
  | 'regulations';

function NetworkSetup({ selectedFieldPath }: Props) {
  const queryClient = useQueryClient();
  const { setShowSetupNetworkScreen } = useOnBoardingContext();
  const { clearSavedData, markDataSaved } = useNetworkDetailsDraft();

  const [selectedTab, setSelectedTab] = useState<TabScreens>('departments');
  const [showDraftSuccessDialog, setShowDraftSuccessDialog] = useState(false);
  const [showActiveConfirmationDialog, setShowActiveConfirmationDialog] =
    useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const navigate = useNavigate();

  const { watch, getValues, setValue, trigger, formState: {isDirty} } =
    useFormContext<NetworkDetailsFormValues>();
  const { showSnackbar } = useSnackbar();

  const name = watch(`${selectedFieldPath}.name`) || 'שם';

  const isFilledDept = (d: any) => departmentSchema.safeParse(d).success;
  const isFilledRole = (r: any) => roleSchema.safeParse(r).success;
  const isFilledShift = (s: any) => shiftSchema.safeParse(s).success;
  const isFilledZone = (z: any) => zoneSchema.safeParse(z).success;
  const isFilledChecklist = (c: any) => checklistSchema.safeParse(c).success;
  const isFilledRegulation = (r: any) => regulationSchema.safeParse(r).success;

  // Returns true if tab can be left; cleans up invalid items as a side-effect.
  const cleanupTab = async (tab: TabScreens) => {
    const node = getValues(selectedFieldPath as any) as any;
    if (!node) return true;

    switch (tab) {
      case 'departments': {
        const all = (node.departments ?? []) as any[];
        const valid = all.filter(isFilledDept);
        if (valid.length < 1) {
          showSnackbar({
            message: 'יש למלא לפחות מחלקה אחת תקינה',
            type: 'error',
          });
          return false;
        }
        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.departments` as any, valid);
        return true;
      }
      case 'roles': {
        const all = (node.roles ?? []) as any[];
        const valid = all.filter(isFilledRole);
        if (valid.length < 1) {
          showSnackbar({
            message: 'יש להגדיר לפחות תפקיד אחד תקין',
            type: 'error',
          });
          return false;
        }
        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.roles` as any, valid);
        return true;
      }
      case 'shifts': {
        const all = (node.shifts ?? []) as any[];
        const valid = all.filter(isFilledShift);
        if (valid.length < 1) {
          showSnackbar({
            message: 'יש להגדיר לפחות משמרת אחת תקינה',
            type: 'error',
          });
          return false;
        }
        const paths = [
          `${selectedFieldPath}.checklistNotesReset`,
          `${selectedFieldPath}.operatingHours`,
        ];
        const isValid = await trigger(paths as any);
        if (!isValid) return false;

        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.shifts` as any, valid);
        return true;
      }
      case 'zones': {
        const all = (node.zones ?? []) as any[];
        const valid = all.filter(isFilledZone);
        if (valid.length < 1) {
          showSnackbar({
            message: 'יש להגדיר לפחות אזור אחד תקין',
            type: 'error',
          });
          return false;
        }
        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.zones` as any, valid);
        return true;
      }
      case 'checklists': {
        const all = (node.checklists ?? []) as any[];
        const valid = all.filter(isFilledChecklist);
        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.checklists` as any, valid);
        return true;
      }
      case 'regulations': {
        const all = (node.regulations ?? []) as any[];
        const valid = all.filter(isFilledRegulation);
        if (valid.length < all.length)
          setValue(`${selectedFieldPath}.regulations` as any, valid);
        return true;
      }
      default:
        return true;
    }
  };

  const CLEANUP_TABS = new Set<TabScreens>([
    'departments',
    'roles',
    'shifts',
    'zones',
    'checklists',
    'regulations',
  ]);

  const handleTabChange = async (newTab: TabScreens) => {
    const newTabIdx = SIDEBAR_TABS.findIndex((t) => t.id === newTab);
    const currentTabIdx = SIDEBAR_TABS.findIndex((t) => t.id === selectedTab);

    if (newTabIdx > currentTabIdx) {
      if (CLEANUP_TABS.has(selectedTab)) {
        if (!(await cleanupTab(selectedTab))) return;
      }
      const isValid = await trigger(
        `${selectedFieldPath}.${selectedTab}` as any,
      );
      if (!isValid) return;
    }

    setSelectedTab(newTab);
  };

  const currentTabIndex = SIDEBAR_TABS.findIndex((t) => t.id === selectedTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === SIDEBAR_TABS.length - 1;

  const handleNext = () => {
    if (isLastTab) return;
    void handleTabChange(SIDEBAR_TABS[currentTabIndex + 1].id);
  };

  const handlePrev = () => {
    if (isFirstTab) return;
    void handleTabChange(SIDEBAR_TABS[currentTabIndex - 1].id);
  };

  const closeNetworkSetup = () => {
    setShowSetupNetworkScreen(false);
  };

  const renderCurrentTabHeader = () => {
    switch (selectedTab) {
      case 'departments':
        return <DepartmentHeader selectedFieldPath={selectedFieldPath} />;
      case 'roles':
        return <RolesHeader selectedFieldPath={selectedFieldPath} />;
      case 'shifts':
        return <ShiftsHeader selectedFieldPath={selectedFieldPath} />;
      case 'zones':
        return <ZonesHeader selectedFieldPath={selectedFieldPath}/>;
      case 'users':
        return <UsersHeader />;
      case 'checklists':
        return <ChecklistsHeader selectedFieldPath={selectedFieldPath} />;
      case 'regulations':
        return <RegulationsHeader />;
      default:
        return null;
    }
  };

  const renderCurrentTab = () => {
    switch (selectedTab) {
      case 'departments':
        return <Departments selectedFieldPath={selectedFieldPath} />;
      case 'roles':
        return <Roles selectedFieldPath={selectedFieldPath} />;
      case 'shifts':
        return <Shifts selectedFieldPath={selectedFieldPath} />;
      case 'zones':
        return <Zones selectedFieldPath={selectedFieldPath} />;
      case 'users':
        return <Users selectedFieldPath={selectedFieldPath} />;
      case 'checklists':
        return <Checklists selectedFieldPath={selectedFieldPath} />;
      case 'regulations':
        return <Regulations selectedFieldPath={selectedFieldPath} />;
      default:
        return null;
    }
  };

  const { mutate: handleUpsertDraft, isPending: isUpsertPending } = useMutation(
    {
      mutationFn: handleUpsertOnBoardingDraftService,
      onSuccess: async (_data, variables) => {
        await markDataSaved(variables.data);
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
        });

        if (variables.isValidData) {
          setShowActiveConfirmationDialog(true);
        } else {
          setShowDraftSuccessDialog(true);
        }
      },
    },
  );

  const { mutate: handleActivateDraft, isPending: isActivatePending } =
    useMutation({
      mutationFn: handleActivateOnBoardingDraftService,
      onSuccess: async () => {
        // Only the network owner's own stored user carries the onboarding flag;
        // a super admin activates on behalf of the owner.
        const user = localStorage.getItem('user')
          ? JSON.parse(localStorage.getItem('user') as string)
          : null;
        if (user && !isSuperAdminOnly()) {
          user.completedOnboarding = true;
          localStorage.setItem('user', JSON.stringify(user));
        }
        await clearSavedData();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT],
        });
        handleNavigateToOperations();
      },
    });

  const handleCloseDraftSuccessDialog = () => {
    setShowDraftSuccessDialog(false);
    trigger();
  };

  const handleCloseActiveConfirmationDialog = () => {
    setShowActiveConfirmationDialog(false);
    trigger();
  };

  const handleFormSubmit = async () => {
    if (isUploadingFiles || isUpsertPending || isActivatePending) return;
    try {
      setIsUploadingFiles(true);
      const cleanedNetwork = filterInvalidNetworkTree(getValues('network'));
      setValue('network', cleanedNetwork, { shouldDirty: true });

      // setValue on the root 'network' path updates getValues()/trigger(),
      // but doesn't resync a currently mounted useFieldArray (e.g. the
      // active tab's list) — that only resyncs from setValue on its exact
      // registered path, same as cleanupTab does per-tab on tab switch.
      const activeNode = getValues(selectedFieldPath as any) as any;
      (
        [
          'departments',
          'roles',
          'shifts',
          'zones',
          'checklists',
          'regulations',
        ] as const
      ).forEach((key) => {
        setValue(`${selectedFieldPath}.${key}` as any, activeNode[key]);
      });

      const isValidAfterCleanup = await trigger();
      const transformed = await buildOnBoardingDraftPayload(cleanedNetwork);
      handleUpsertDraft({
        data: transformed,
        isValidData: isValidAfterCleanup,
      });
    } catch (error) {
      console.error('On-boarding draft upload failed:', error);
      showSnackbar({
        message: 'שגיאה בהעלאת קבצים. נסו שוב.',
        type: 'error',
      });
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleNavigateToOperations = () => {
    closeNetworkSetup();
    navigate(-1);
  };

  return (
    <>
      {(isUploadingFiles || isUpsertPending || isActivatePending) && (
        <FullScreenLoader open />
      )}

      <div className="w-full h-full py-4">
        <div className="w-full h-full flex flex-col">
          <div className="px-8 flex flex-col gap-2 pb-1 border-b border-b-gray80">
            <div
              className="flex items-center max-w-fit gap-3 cursor-pointer"
              onClick={closeNetworkSetup}
            >
              <button type="button" className="w-4 h-4">
                <img src={SVG.goBackIcon} />
              </button>

              <p className="m-0 text-xl font-bold text-black">{name}</p>
            </div>

            {renderCurrentTabHeader()}
          </div>

          <div className="mt-3 px-8 flex-1 flex gap-4 min-h-0 overflow-y-hidden">
            <ScreensSidebar
              selectedTab={selectedTab}
              selectedFieldPath={selectedFieldPath}
              handleTabChange={handleTabChange}
            />

            {renderCurrentTab()}
          </div>

          <div className="mt-1 px-8 pt-2.5 flex justify-between items-center gap-3 border-t border-t-gray80">
            <NetworkSetupNavBar
              currentStep={currentTabIndex + 1}
              totalSteps={SIDEBAR_TABS.length}
              isFirstTab={isFirstTab}
              isLastTab={isLastTab}
              onNext={handleNext}
              onPrev={handlePrev}
            />

            <div className="w-[1px] h-[34px] bg-gray70" />

            <div className="flex items-center gap-3">
              <AppButton onClick={closeNetworkSetup}>ביטול</AppButton>

              <AppButton
                variant="primary"
                disabled={
                  isUploadingFiles || isUpsertPending || isActivatePending || !isDirty
                }
                onClick={handleFormSubmit}
              >
                שמירה ויציאה
              </AppButton>
            </div>
          </div>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDraftSuccessDialog}
        onClose={handleCloseDraftSuccessDialog}
        icon={
          <img
            src={SVG.infoBlueIcon}
            alt="icon"
            className="w-[46px] h-[46px] object-contain"
          />
        }
        bordered={false}
        containerClasses="!px-2"
        title="המערכת עדיין לא מוכנה"
        subTitle={
          <div className="flex flex-col gap-2">
            <p className="m-0 text-base text-darkGray">התוכן שהעלתם ישמר.</p>
            <p className="m-0 text-base text-darkGray">
              השלימו את שדות החובה כדי להפעיל
            </p>
            <p className="m-0 text-base text-darkGray">תוכלו להשלים בכל עת.</p>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 mt-4">
            <AppButton onClick={handleCloseDraftSuccessDialog}>ביטול</AppButton>

            <AppButton variant="primary" onClick={handleNavigateToOperations}>
              שמירה
            </AppButton>
          </div>
        }
      />

      <AppConfirmationDialog
        open={showActiveConfirmationDialog}
        onClose={handleCloseActiveConfirmationDialog}
        icon={
          <img
            src={SVG.activateCheckIcon}
            alt="icon"
            className="w-[56px] h-[56px] object-contain"
          />
        }
        width={484}
        bordered={false}
        containerClasses="!px-2"
        title="ברכות, המערכת מוכנה"
        subTitle={
          <div className="flex flex-col gap-2 max-w-[264px]">
            <p className="m-0 text-base text-center text-darkGray">
              אתם יכולים להתחיל.
            </p>
            <p className="m-0 text-base text-center text-darkGray">
              בלחיצה על "הפעלת המערכת" ישלחו לכל המשתמשים שהגדרתם לינקים להורדת
              האפליקציה.
            </p>
            <p className="m-0 text-base text-center text-darkGray">
              תוכלו להפעיל בכל שלב שתבחרו.
            </p>
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 mt-4">
            <AppButton onClick={handleCloseActiveConfirmationDialog}>
              ביטול
            </AppButton>

            <AppButton onClick={handleNavigateToOperations}>
              יציאה ללא הפעלה
            </AppButton>

            <AppButton variant="primary" onClick={() => handleActivateDraft()}>
              הפעלת המערכת
            </AppButton>
          </div>
        }
      />
    </>
  );
}

export default NetworkSetup;
